import React, {Component, ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../../components/shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import {ApplyHistoryInfo, OwnershipTransferDetail} from '../repository/NatGetOwnershipTransfer'
import {PageFooterArea, PageFotterAreaLeft, PageMainArea} from '../../../../components/shared/hoc/withAdminPageTemplate'
import {StyledTableCaption} from '../../../../styledComponents/adminPage/Table'
import {NatOwnershipTransferStatus, OwnershipTransferProcess} from './NatOwnershipTransferStatus'
import {NatOwnershipTransferApplyDetailByIntermediary} from './NatOwnershipTransferApplyDetailByIntermediary'
import {NatOwnershipTransferApplyDetailByOldMember} from './NatOwnershipTransferApplyDetailByOldMember'
import {NatOwnershipTransferApplyDetailByNewMember} from './NatOwnershipTransferApplyDetailByNewMember'
import {NatOwnershipTransferIssueDetail} from './NatOwnershipTransferIssueDetail'
import {HeadingAreaMessageParams} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponent'
import {BackLink} from '../../../../styledComponents/adminPage/Links'
import {OWNERSHIP_TRANSFER_STATUS, ROLE_FOR_ADMINISTRATOR_REAL, ROLE_FOR_GUEST} from '../../../../CodeMaster'
import {Location} from 'react-router-dom'

import {Table, TableBody} from '@mui/material'

interface Props {
    condominiumCd: string
    applyId: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    ownershipTransferDetail: Nullable<OwnershipTransferDetail>
    isLoadCompleted: boolean
}

class NatOwnershipTransferPage extends Component<Props & AdminPagePropsSet, States> {
    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.state = {
            ownershipTransferDetail: null,
            isLoadCompleted: false,
        }
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const ownershipTransferDetail = await this.getOwnershipTransfer()
            this.setState({ownershipTransferDetail: ownershipTransferDetail})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
        this.showInitialMessage()
    }

    showInitialMessage(): void {
        if (!this.props.location || !this.props.location.state) {
            return
        }
        const initialMessage: HeadingAreaMessageParams = this.props.location.state.initialMessage
        this.props.navigate(this.props.location.pathname, {replace: true})
        this.props.eventEmitter.showHeadingAreaMessage(initialMessage)
    }

    async getOwnershipTransfer(): Promise<Nullable<OwnershipTransferDetail>> {
        const {condominiumCd, applyId, ownershipTransfersRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            ownershipTransfersRepo.getOwnershipTransfer.bind(ownershipTransfersRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    convertToOwnershipTransferProcess(applyHistoryInfoList: ApplyHistoryInfo[]): OwnershipTransferProcess[] {
        const issueInfo = applyHistoryInfoList
            .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.ISSUED.value)
        const issueRepRole = issueInfo ? ROLE_FOR_ADMINISTRATOR_REAL.codeToEnumConstants(issueInfo.userRoleId).label : ''
        const issueProcess = {
            processName: '発行',
            rep: [{rep: `${issueRepRole}　${issueInfo?.userFamilyName} ${issueInfo?.userFirstName}`}],
            processLatestDateTime: issueInfo?.registerDatetime,
            completed: !!issueInfo,
        }

        const inputInfoList = this.getInputInfoList(applyHistoryInfoList)
        const completeInfo = applyHistoryInfoList
            .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value)
        const invalidInfo = applyHistoryInfoList
            .find(history => history.applyStatus === OWNERSHIP_TRANSFER_STATUS.INVALID.value)

        const inputProcess = {
            processName: '入力',
            rep: [
                {
                    rep: '仲介業者',
                    inputCompleteDateTime: inputInfoList.find(issue =>
                        issue.userRoleId === ROLE_FOR_GUEST.INTERMEDIARY.value)
                        ?.registerDatetime,
                },
                {
                    rep: '旧組合員',
                    inputCompleteDateTime: inputInfoList.find(issue =>
                        issue.userRoleId === ROLE_FOR_GUEST.OLD_MEMBER.value)
                        ?.registerDatetime,
                },
                {
                    rep: '新組合員',
                    inputCompleteDateTime: inputInfoList.find(issue =>
                        issue.userRoleId === ROLE_FOR_GUEST.NEW_MEMBER.value)
                        ?.registerDatetime,
                },
            ],
            processLatestDateTime: inputInfoList.sort((a, b) =>
                b.historyId - a.historyId)[0]?.registerDatetime,
            completed: !!completeInfo || !!invalidInfo,
        }

        if (!!invalidInfo) {
            const invalidProcess = {
                processName: '無効',
                rep: [],
                processLatestDateTime: invalidInfo?.registerDatetime,
                completed: !!invalidInfo,
                memo: invalidInfo?.memo,
            }
            return [issueProcess, invalidProcess]
        }

        const completeProcess = {
            processName: '提出完了',
            rep: [],
            processLatestDateTime: completeInfo?.registerDatetime,
            completed: !!completeInfo,
        }
        return [issueProcess, inputProcess, completeProcess]
    }

    getInputInfoList(applyHistoryInfoList: ApplyHistoryInfo[]): ApplyHistoryInfo[] {
        const intermediaryHistoryId = applyHistoryInfoList
            .filter(history => history.userRoleId === ROLE_FOR_GUEST.INTERMEDIARY.value)
            ?.sort((a, b) => b.historyId - a.historyId)[0]
            ?.historyId

        return applyHistoryInfoList
            .filter(history => history.historyId >= intermediaryHistoryId &&
                (history.applyStatus === OWNERSHIP_TRANSFER_STATUS.INPUTTING.value ||
                    history.applyStatus === OWNERSHIP_TRANSFER_STATUS.COMPLETE.value))
    }

    handleDownloadClick(path: string): void {
        const tempLink: HTMLAnchorElement = document.createElement('a')
        tempLink.href = path
        document.body.appendChild(tempLink)
        tempLink.click()
        document.body.removeChild(tempLink)
    }

    renderIssueTable(): ReactNode {
        if (!this.state.ownershipTransferDetail) {
            return <></>
        }
        const {
            applyId,
            applyStatus,
            condominiumName,
            ridgeName,
            roomNumber,
            intermediaryName,
            issueFile,
            issueRepFamilyName,
            issueRepFirstName,
            issueDatetime,
        } = this.state.ownershipTransferDetail

        return (
            <>
                <Table>
                    <StyledTableCaption>発行内容</StyledTableCaption>
                    <TableBody>
                        <NatOwnershipTransferIssueDetail
                            applyId={applyId}
                            applyStatus={applyStatus}
                            condominiumName={condominiumName}
                            ridgeName={ridgeName}
                            roomNumber={roomNumber}
                            intermediaryName={intermediaryName}
                            issueRepFamilyName={issueRepFamilyName}
                            issueRepFirstName={issueRepFirstName}
                            issueDatetime={issueDatetime}
                            issueFile={issueFile}
                            handleDownloadClick={this.handleDownloadClick}
                        />
                    </TableBody>
                </Table>
            </>
        )
    }

    renderApplyStatus(): Nullable<ReactElement> {
        if (!this.state.ownershipTransferDetail) {
            return <></>
        }
        const {applyHistoryInfo} = this.state.ownershipTransferDetail

        return (
            <>
                <Table>
                    <StyledTableCaption>進行状況</StyledTableCaption>
                </Table>
                <NatOwnershipTransferStatus
                    process={this.convertToOwnershipTransferProcess(applyHistoryInfo)}
                />
            </>
        )
    }

    renderApplyTable(): Nullable<ReactElement> {
        if (!this.state.ownershipTransferDetail) {
            return <></>
        }
        const {intermediaryInfo, oldMemberInfo, newMemberInfo, applyHistoryInfo} = this.state.ownershipTransferDetail
        const inputInfoList = this.getInputInfoList(applyHistoryInfo)
        return (
            <>
                <Table>
                    <StyledTableCaption>申請内容</StyledTableCaption>
                    <TableBody>
                        <NatOwnershipTransferApplyDetailByIntermediary
                            intermediaryInfo={intermediaryInfo}
                        />
                        <NatOwnershipTransferApplyDetailByOldMember
                            oldMemberInfo={
                                inputInfoList.some(issue => issue.userRoleId === ROLE_FOR_GUEST.OLD_MEMBER.value)
                                    ? oldMemberInfo
                                    : null
                            }
                        />
                        <NatOwnershipTransferApplyDetailByNewMember
                            newMemberInfo={
                                inputInfoList.some(issue => issue.userRoleId === ROLE_FOR_GUEST.NEW_MEMBER.value)
                                    ? newMemberInfo
                                    : null
                            }
                        />
                    </TableBody>
                </Table>
            </>
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return <>
            <PageMainArea>
                {this.renderIssueTable()}
                {this.renderApplyStatus()}
                {this.renderApplyTable()}
            </PageMainArea>
            <PageFooterArea>
                <PageFotterAreaLeft>
                    <BackLink
                        onClick={() => this.props.navigate(
                            `/admin/applications/ownershipTransferList`,
                        )}
                    >
                        組合員資格変更届一覧へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </PageFooterArea>
        </>
    }
}

export default createAdminPage(NatOwnershipTransferPage)
